import React, { createContext, useContext, useRef, useEffect, useCallback } from 'react'
import { EventCallback, Middleware } from './types'

export class EventBus{
    private events: Map<string, Set<EventCallback>> = new Map()
    private middlewares: Map<string, Middleware[]> = new Map()
    private onceEvents: Map<string, Set<EventCallback>> = new Map()

    use(event: string, middleware: Middleware) {
        if (!this.middlewares.has(event)) {
            this.middlewares.set(event, [])
        }

        this.middlewares.get(event)!.push(middleware)
    }

    private applyMiddlewares<T>(event: string, data: T, callback: (data: T) => void) {
        const middlewares = this.middlewares.get(event) || [];
        
        const execute = (index: number, currentData: T) => {
        if (index >= middlewares.length) {
            callback(currentData)
            return
        }
        
        middlewares[index](currentData, (newData) => {
            execute(index + 1, newData)
        })}
        
        execute(0, data)
    }

    on<T>(event: string, callback: EventCallback<T>): () => void {
        if (!this.events.has(event)) {
            this.events.set(event, new Set())
        }

        this.events.get(event)!.add(callback as EventCallback)
    
        return () => this.off(event, callback)
    }

    once<T>(event: string, callback: EventCallback<T>): () => void {
        if (!this.onceEvents.has(event)) {
            this.onceEvents.set(event, new Set())
        }

        const onceWrapper = (data: T) => {
            callback(data)
            this.off(event, onceWrapper)
        }

        this.onceEvents.get(event)!.add(onceWrapper as EventCallback)
        this.on(event, onceWrapper)

        return () => this.off(event, onceWrapper)
    }

    off<T>(event: string, callback: EventCallback<T>): void {
        const callbacks = this.events.get(event)
        if (callbacks) {
            callbacks.delete(callback as EventCallback)
            if (callbacks.size === 0) {
                this.events.delete(event)
            }
        }
    }

    emit<T>(event: string, data?: T): void {
        const callbacks = this.events.get(event)
        if (callbacks) {
            callbacks.forEach(callback => {
                this.applyMiddlewares(event, data, (processedData) => {
                    try {
                    callback(processedData)
                    } catch (error) {
                    console.error(`Error in event handler for "${event}":`, error)
                    }
                })
            })
        }
    }

    getActiveEvents(): string[] {
        return Array.from(this.events.keys())
    }

    clear(event?: string): void {
        if (event) {
            this.events.delete(event)
            this.onceEvents.delete(event)
        } else {
            this.events.clear()
            this.onceEvents.clear()
        }
    }
}

export const eventBus = new EventBus()